const SP_BASE = 'https://litehouseinc.sharepoint.com/sites/AIHQ/_api/web';
const LIST_TITLE = "AI Wins & Experiments";
const LIST_ENCODED = encodeURIComponent(`'${LIST_TITLE}'`);
const LIKES_LIST = "AI Wins Likes";
const LIKES_ENCODED = encodeURIComponent(`'${LIKES_LIST}'`);
const SP_HOST = 'https://litehouseinc.sharepoint.com';

async function spFetch(url, accessToken, options = {}) {
  const res = await fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json;odata=nometadata',
      'Content-Type': 'application/json;odata=nometadata',
      ...options.headers,
    },
  });
  if (!res.ok) {
    const err = await res.text();
    throw new Error(`SharePoint API error ${res.status}: ${err}`);
  }
  if (res.status === 204) return null;
  return res.json();
}

const GRAPH_BASE = 'https://graph.microsoft.com/v1.0';
const USER_GROUP_NAME = 'All Litehouse';

let _groupIdCache = null;

async function getGroupId(accessToken) {
  if (_groupIdCache) return _groupIdCache;
  const res = await fetch(
    `${GRAPH_BASE}/groups?$filter=displayName eq '${encodeURIComponent(USER_GROUP_NAME)}'&$select=id,displayName`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: 'application/json',
      },
    }
  );
  if (!res.ok) return null;
  const data = await res.json();
  _groupIdCache = data.value?.[0]?.id ?? null;
  return _groupIdCache;
}

export async function getCurrentUserDepartment(accessToken) {
  const res = await fetch(`${GRAPH_BASE}/me?$select=department`, {
    headers: { Authorization: `Bearer ${accessToken}`, Accept: 'application/json' },
  });
  if (!res.ok) return '';
  const data = await res.json();
  return data.department || '';
}

export async function getUserDepartmentFromProfile(accessToken, email) {
  const accountName = `i:0#.f|membership|${email}`;
  const params = new URLSearchParams({ '@v': `'${accountName}'`, '@n': "'Department'" });
  const url = `${SP_HOST}/_api/SP.UserProfiles.PeopleManager/GetUserProfilePropertyFor(accountName=@v,propertyName=@n)?${params}`;
  const res = await fetch(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json;odata=nometadata',
    },
  });
  if (!res.ok) return '';
  const data = await res.json();
  return data.value || '';
}

export async function searchUsers(accessToken, query) {
  if (!query || query.length < 2) return [];
  const encoded = encodeURIComponent(query);

  const search = `"displayName:${encoded}" OR "mail:${encoded}"`;
  const groupId = await getGroupId(accessToken);
  const url = groupId
    ? `${GRAPH_BASE}/groups/${groupId}/members/microsoft.graph.user?$search=${encodeURIComponent(search)}&$select=id,displayName,mail,department&$top=10`
    : `${GRAPH_BASE}/users?$search=${encodeURIComponent(search)}&$select=id,displayName,mail,department&$top=10`;

  const res = await fetch(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json',
      ConsistencyLevel: 'eventual',
    },
  });
  if (!res.ok) return [];
  const data = await res.json();
  return (data.value || []).filter((u) => u.mail?.toLowerCase().endsWith('@litehouseinc.com'));
}

export async function getSiteId(accessToken) {
  const data = await spFetch(`${SP_BASE}/title`, accessToken);
  return data.value || 'AIHQ';
}

// Verifies the list exists and returns its title (no auto-create)
export async function ensureList(accessToken, siteId) {
  const data = await spFetch(
    `${SP_BASE}/lists/getByTitle(${LIST_ENCODED})?$select=Id,Title`,
    accessToken
  );
  return data.Id;
}

export async function submitWin(accessToken, siteId, listId, data) {
  const user = await spFetch(`${SP_BASE}/currentuser?$select=Id`, accessToken);

  // Resolve OnBehalfOf to a SharePoint user ID if an email was provided
  let onBehalfOfId = null;
  if (data.onBehalfOfEmail) {
    try {
      const ensured = await spFetch(`${SP_BASE}/ensureuser`, accessToken, {
        method: 'POST',
        body: JSON.stringify({ logonName: data.onBehalfOfEmail }),
      });
      onBehalfOfId = ensured?.Id ?? null;
    } catch {
      // fall through — leave OnBehalfOf unset rather than blocking submission
    }
  }

  const body = {
    Title: data.task.replace(/[\r\n]+/g, ' ').trim().slice(0, 255),
    Playbook: data.howDidYouUseAI,
    // "Time Spent Using AI" was removed from the form; new records default to 0.
    // Historical values are untouched (this only writes new items).
    TimeSpent: String(data.timeSpent ?? 0),
    TimeSaved: String(data.timeSaved),
    Frequency: data.frequency,
    Department: data.department || '',
    Benefits: data.benefits || '',
    SubmittedById: user.Id,
    SubmissionDate: new Date().toISOString(),
  };

  if (onBehalfOfId) {
    body.OnBehalfOfId = onBehalfOfId;
  }

  const item = await spFetch(
    `${SP_BASE}/lists/getByTitle(${LIST_ENCODED})/items`,
    accessToken,
    { method: 'POST', body: JSON.stringify(body) }
  );

  return item; // includes Id needed for attachment uploads
}

export async function getDeletedWins(accessToken) {
  const data = await spFetch(
    `${SP_BASE}/lists/getByTitle(${LIST_ENCODED})/items` +
      `?$select=Id,Title,Playbook,TimeSpent,TimeSaved,Frequency,Department,OnBehalfOf/Title,Benefits,SubmittedBy/Title,Author/Title,Created` +
      `&$expand=SubmittedBy,Author,OnBehalfOf` +
      `&$filter=IsDeleted eq 1` +
      `&$orderby=Created desc` +
      `&$top=500`,
    accessToken
  );
  return data.value.map((item) => ({
    id: item.Id,
    task: item.Title,
    howDidYouUseAI: item.Playbook || '',
    timeSavedMinutes: parseFloat(item.TimeSaved) || 0,
    timeSpentMinutes: parseFloat(item.TimeSpent) || 0,
    frequency: item.Frequency,
    department: item.Department || '',
    onBehalfOf: item.OnBehalfOf?.Title || '',
    benefits: item.Benefits || '',
    submittedBy: item.SubmittedBy?.Title || item.Author?.Title || '',
    created: item.Created,
  }));
}

export async function restoreWin(accessToken, itemId) {
  return spFetch(
    `${SP_BASE}/lists/getByTitle(${LIST_ENCODED})/items(${itemId})`,
    accessToken,
    {
      method: 'POST',
      headers: {
        'X-HTTP-Method': 'MERGE',
        'IF-MATCH': '*',
      },
      body: JSON.stringify({ IsDeleted: false }),
    }
  );
}

export async function deleteWin(accessToken, itemId) {
  return spFetch(
    `${SP_BASE}/lists/getByTitle(${LIST_ENCODED})/items(${itemId})`,
    accessToken,
    {
      method: 'POST',
      headers: {
        'X-HTTP-Method': 'MERGE',
        'IF-MATCH': '*',
      },
      body: JSON.stringify({ IsDeleted: true }),
    }
  );
}

export async function updateWin(accessToken, itemId, data) {
  return spFetch(
    `${SP_BASE}/lists/getByTitle(${LIST_ENCODED})/items(${itemId})`,
    accessToken,
    {
      method: 'POST',
      headers: {
        'X-HTTP-Method': 'MERGE',
        'IF-MATCH': '*',
      },
      body: JSON.stringify({
        Title: data.task.replace(/[\r\n]+/g, ' ').trim().slice(0, 255),
        Playbook: data.howDidYouUseAI,
        TimeSpent: String(data.timeSpent ?? 0),
        TimeSaved: String(data.timeSaved),
        Frequency: data.frequency,
        Department: data.department || '',
        Benefits: data.benefits || '',
      }),
    }
  );
}

export async function uploadAttachment(accessToken, itemId, file) {
  const fileName = encodeURIComponent(file.name);
  const arrayBuffer = await file.arrayBuffer();
  return spFetch(
    `${SP_BASE}/lists/getByTitle(${LIST_ENCODED})/items(${itemId})/AttachmentFiles/add(FileName='${fileName}')`,
    accessToken,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/octet-stream' },
      body: arrayBuffer,
    }
  );
}

export async function deleteAttachment(accessToken, itemId, fileName) {
  return spFetch(
    `${SP_BASE}/lists/getByTitle(${LIST_ENCODED})/items(${itemId})/AttachmentFiles/getByFileName('${encodeURIComponent(fileName)}')`,
    accessToken,
    {
      method: 'POST',
      headers: {
        'X-HTTP-Method': 'DELETE',
        'IF-MATCH': '*',
      },
    }
  );
}

export async function getAttachments(accessToken, itemId) {
  const data = await spFetch(
    `${SP_BASE}/lists/getByTitle(${LIST_ENCODED})/items(${itemId})/AttachmentFiles`,
    accessToken
  );
  return (data.value || []).map((f) => ({
    name: f.FileName,
    url: SP_HOST + f.ServerRelativeUrl,
  }));
}

// ----- Likes -----

export async function getUserLikes(accessToken) {
  const user = await spFetch(`${SP_BASE}/currentuser?$select=Id`, accessToken);
  const data = await spFetch(
    `${SP_BASE}/lists/getByTitle(${LIKES_ENCODED})/items` +
      `?$select=Id,WinId&$filter=LikedById eq ${user.Id}&$top=500`,
    accessToken
  );
  return new Set((data.value || []).map((item) => String(item.WinId)));
}

export async function addLike(accessToken, winId) {
  const user = await spFetch(`${SP_BASE}/currentuser?$select=Id`, accessToken);
  return spFetch(
    `${SP_BASE}/lists/getByTitle(${LIKES_ENCODED})/items`,
    accessToken,
    {
      method: 'POST',
      body: JSON.stringify({ Title: String(winId), WinId: String(winId), LikedById: user.Id }),
    }
  );
}

export async function removeLike(accessToken, winId) {
  const user = await spFetch(`${SP_BASE}/currentuser?$select=Id`, accessToken);
  const data = await spFetch(
    `${SP_BASE}/lists/getByTitle(${LIKES_ENCODED})/items` +
      `?$select=Id&$filter=WinId eq '${winId}' and LikedById eq ${user.Id}&$top=1`,
    accessToken
  );
  const item = data.value?.[0];
  if (!item) return;
  return spFetch(
    `${SP_BASE}/lists/getByTitle(${LIKES_ENCODED})/items(${item.Id})`,
    accessToken,
    { method: 'POST', headers: { 'X-HTTP-Method': 'DELETE', 'IF-MATCH': '*' } }
  );
}

export async function getAllLikeCounts(accessToken) {
  const data = await spFetch(
    `${SP_BASE}/lists/getByTitle(${LIKES_ENCODED})/items?$select=WinId&$top=500`,
    accessToken
  );
  const counts = {};
  for (const item of data.value || []) {
    const id = String(item.WinId);
    counts[id] = (counts[id] || 0) + 1;
  }
  return counts;
}

export async function getWins(accessToken, siteId, listId) {
  const data = await spFetch(
    `${SP_BASE}/lists/getByTitle(${LIST_ENCODED})/items` +
      `?$select=Id,Title,Playbook,TimeSpent,TimeSaved,Frequency,Department,OnBehalfOf/Title,Benefits,SubmittedBy/Title,Author/Title,Created,IsDeleted` +
      `&$expand=SubmittedBy,Author,OnBehalfOf` +
      `&$filter=IsDeleted ne 1` +
      `&$orderby=Created desc` +
      `&$top=500`,
    accessToken
  );
  return data.value.map((item) => ({
    id: item.Id,
    task: item.Title,
    howDidYouUseAI: item.Playbook || '',
    timeSavedMinutes: parseFloat(item.TimeSaved) || 0,
    timeSpentMinutes: parseFloat(item.TimeSpent) || 0,
    frequency: item.Frequency,
    department: item.Department || '',
    onBehalfOf: item.OnBehalfOf?.Title || '',
    benefits: item.Benefits || '',
    submittedBy: item.SubmittedBy?.Title || item.Author?.Title || '',
    created: item.Created,
  }));
}
